'use client';

import {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';

/**
 * Chrome-safe pathname: null during prerender AND on the first client render,
 * so shell chrome renders byte-identical on both sides (no hydration
 * mismatches, no blocking-prerender-client-hook). The real pathname arrives
 * via a Suspense'd bridge with useLayoutEffect — committed before the next
 * paint on hydration and client navigations alike. The hard-load
 * paint-to-hydration window is covered by the pre-paint scripts + globals.css
 * §"Pre-hydration chrome" guards, retired here via data-chrome-hydrated.
 *
 * Consumers deriving VISUALS must render neutrally for null and pair with a
 * CSS guard; consumers needing transition semantics (scroll reset, progress
 * bar) must keep the real usePathname() inside their own <Suspense> instead —
 * a null→real flip here would read as a fake route change.
 */
const ChromePathnameContext = createContext<string | null>(null);

// The bridge renders during SSR streaming; the server pass must not log
// React's useLayoutEffect warning.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function useChromePathname(): string | null {
  return useContext(ChromePathnameContext);
}

function PathnameBridge({ onChange }: { onChange: (pathname: string) => void }) {
  const pathname = usePathname();
  useIsomorphicLayoutEffect(() => {
    if (pathname !== null) onChange(pathname);
  }, [pathname, onChange]);
  return null;
}

export function ChromePathnameProvider({ children }: { children: ReactNode }) {
  const [pathname, setPathname] = useState<string | null>(null);

  // Retires the pre-hydration CSS guards only after chrome resolved against a
  // real pathname and that render committed (passive effects run post-paint
  // of the resolved tree).
  useEffect(() => {
    if (pathname !== null) {
      document.documentElement.dataset.chromeHydrated = '1';
    }
  }, [pathname]);

  return (
    <ChromePathnameContext.Provider value={pathname}>
      <Suspense fallback={null}>
        <PathnameBridge onChange={setPathname} />
      </Suspense>
      {children}
    </ChromePathnameContext.Provider>
  );
}
